// Порядок стилей важен: собственные правила перекрывают GridStack.
import 'gridstack/dist/gridstack.min.css';
import './styles.css';

import { api } from './api';
import { auth, initAuth, isAnonymous } from './auth';
import { tr } from './ui-i18n';

import { ensureAppShell } from './app/shell';
import { refreshIcons, escapeHtml } from './app/dom';
import { state } from './app/state';
import { controls } from './app/controls';
import { currentLocale, applyStaticLocale } from './app/locale';
import { setUiLocale } from './ui-i18n';
import { applyTheme, renderThemePresets, renderAutoColorPresets } from './app/theming';
import { narrowWorkspaceMedia, phoneWorkspaceMedia, scheduleGridMetricsUpdate, setLayoutEditing } from './app/canvas';
import { setBuilderOpen, setPaletteOpen, setViewMode } from './app/workspace';
import { setStatus, markDirty, errorMessage } from './app/status';
import { populateDashboardSelect, loadDashboard } from './app/dashboards';
import { loadIndicatorSections, populateIndicators } from './app/indicators';
import { updateIndicatorControls } from './app/widget-form';
import { renderDashboardGrid } from './app/widgets';
import { bindSettingsEvents } from './app/events/settings';
import { bindWidgetBuilderEvents } from './app/events/widget-builder';
import { bindAutoBuilderEvents } from './app/events/autobuilder';
import { bindSourceCatalogEvents } from './app/events/sources';
import { bindDashboardEvents } from './app/events/dashboards';
import { bindWorkspaceEvents } from './app/events/workspace';
import { bindCanvasEvents } from './app/events/canvas';
import { applyAuthUi, bindAuthEvents } from './app/auth-ui';

ensureAppShell();

function setupEvents(): void {
  // Порядок важен только там, где несколько групп слушают один элемент
  // (document click): он тот же, что был в едином setupEvents().
  bindSettingsEvents();
  bindWidgetBuilderEvents();
  bindAutoBuilderEvents();
  bindSourceCatalogEvents();
  bindDashboardEvents();
  bindWorkspaceEvents();
  bindCanvasEvents();
  bindAuthEvents();
}

async function initialize(): Promise<void> {
  refreshIcons(document);
  setupEvents();
  renderThemePresets();
  renderAutoColorPresets();
  applyTheme();
  setUiLocale(currentLocale());
  applyStaticLocale();
  setPaletteOpen(!narrowWorkspaceMedia.matches);
  setBuilderOpen(false);
  setLayoutEditing(!phoneWorkspaceMedia.matches);
  scheduleGridMetricsUpdate();
  setStatus('Загрузка каталогов…');
  try {
    // Вход — до первого запроса данных: от него зависят заголовки запросов
    // и то, что вообще можно запрашивать (гостю каталоги закрыты).
    await initAuth();
    applyAuthUi();
    if (auth().callbackError) setStatus(tr('Не удалось войти: {0}', auth().callbackError ?? ''), 'error');
    const anonymous = isAnonymous();
    if (anonymous) setViewMode(true);
    const [indicators, territories, dashboards] = await Promise.all([
      anonymous ? Promise.resolve([]) : api.indicators(true, currentLocale()),
      anonymous ? Promise.resolve([]) : api.territories({}, currentLocale()),
      api.dashboards(),
    ]);
    state.indicators = indicators;
    state.localTerritories = territories;
    state.territories = [...territories];
    state.catalogTerritories = [...territories];
    state.dashboards = dashboards;
    if (!anonymous) {
      await loadIndicatorSections();
      populateIndicators();
      await updateIndicatorControls();
    }
    populateDashboardSelect();
    if (dashboards.length) {
      const requestedDashboard = new URLSearchParams(window.location.search).get('dashboard');
      const initialDashboardId = dashboards.some((dashboard) => dashboard.id === requestedDashboard)
        ? requestedDashboard
        : dashboards[0]?.id;
      await loadDashboard(initialDashboardId ?? '', true);
    } else if (anonymous) {
      renderDashboardGrid();
      setStatus(tr('Общих дашбордов пока нет. Войдите, чтобы создавать свои дашборды.'));
    } else {
      renderDashboardGrid();
      markDirty(true);
      setStatus('Каталоги загружены. Создайте первый дашборд.', 'success');
    }
  } catch (error) {
    setStatus(`Приложение не инициализировано: ${errorMessage(error)}`, 'error');
    controls.emptyState.innerHTML = `
      <div class="empty-state-icon error">!</div>
      <h3>Нет связи с API</h3>
      <p>${escapeHtml(errorMessage(error))}</p>
      <p>Проверьте, что приложение запущено на http://127.0.0.1:8080/.</p>`;
  }
}

void initialize();
